import React, { useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import { scrapeAndExtractAcceleratorInfo, extractAcceleratorInfoFromText } from '../../services/backend';

const AcceleratorModal = ({ accelerator, setAccelerator, onSave, onClose, creating, isDemo, onDelete }) => {
  // AI processing state
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiMethod, setAiMethod] = useState(''); // 'website' or 'text'
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [acceleratorText, setAcceleratorText] = useState('');
  const [aiError, setAiError] = useState('');

  // AI processing functions
  const handleWebsiteScraping = async () => {
    if (!websiteUrl.trim()) {
      setAiError('Please enter a website URL');
      return;
    }
    
    setAiProcessing(true);
    setAiError('');
    
    try {
      const result = await scrapeAndExtractAcceleratorInfo(websiteUrl);
      if (result.success) {
        setAccelerator(prev => ({
          ...prev,
          ...result.data,
          tier: prev.tier // Keep the existing tier
        }));
        setWebsiteUrl('');
        setAiMethod('');
      } else {
        // Check if it's a scraping failure and suggest text method
        if (result.error && (result.error.includes('CORS') || result.error.includes('unavailable') || result.error.includes('network'))) {
          setAiError(`${result.error} 💡 Try using the "Text Analysis" method instead - you can paste accelerator information directly.`);
        } else {
          setAiError(result.error || 'Failed to extract accelerator information');
        }
      }
    } catch (error) {
      setAiError('An error occurred while processing the website. 💡 Try using the "Text Analysis" method instead.');
      console.error('Error processing website:', error);
    } finally {
      setAiProcessing(false);
    }
  };

  const handleTextExtraction = async () => {
    if (!acceleratorText.trim()) {
      setAiError('Please enter accelerator information');
      return;
    }
    
    setAiProcessing(true);
    setAiError('');
    
    try {
      const result = await extractAcceleratorInfoFromText(acceleratorText);
      if (result.success) {
        setAccelerator(prev => ({
          ...prev,
          ...result.data,
          tier: prev.tier // Keep the existing tier
        }));
        setAcceleratorText('');
        setAiMethod('');
      } else {
        setAiError(result.error || 'Failed to extract accelerator information');
      }
    } catch (error) {
      setAiError('An error occurred while processing the text');
      console.error('Error processing text:', error);
    } finally {
      setAiProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto modal" onClick={e => e.stopPropagation()}>
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={onClose}>
          <X size={24} />
        </button>
        
        <div className="flex items-center mb-4">
          <h2 className="text-xl font-bold flex-1">
            {accelerator.id ? 'Edit Accelerator' : 'Add Accelerator'}
          </h2>
          {isDemo && (
            <span className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
          )}
        </div>

        {isDemo && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 text-sm">
              <strong>Demo Mode:</strong> You cannot edit demo accelerators. Please select a real company to add accelerators.
            </p>
          </div>
        )}

        {/* Quick fill: URL or paste text to populate form */}
        <div className={`mb-6 p-4 sm:p-5 rounded-xl border ${isDemo ? 'bg-gray-50 border-gray-200' : 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'}`}>
          <h3 className="text-base sm:text-lg font-semibold mb-1 text-gray-800">Quick fill form</h3>
          <p className="text-sm text-gray-600 mb-4">Enter a URL or paste accelerator bio text to auto-populate the fields below.</p>
          {isDemo && (
            <p className="text-gray-500 text-sm mb-3">AI features disabled for demo company</p>
          )}
          
          {/* Method Selection - stack on mobile */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
            <button
              onClick={() => setAiMethod('website')}
              disabled={isDemo}
              className={`flex-1 min-w-0 px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-all ${
                aiMethod === 'website' 
                  ? 'bg-orange-600 text-white shadow-lg' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              } ${isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              🌐 Paste URL
            </button>
            <button
              onClick={() => setAiMethod('text')}
              disabled={isDemo}
              className={`flex-1 min-w-0 px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-all ${
                aiMethod === 'text' 
                  ? 'bg-orange-600 text-white shadow-lg' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              } ${isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              📝 Paste bio text
            </button>
          </div>

          {/* Website URL Input - stack on mobile */}
          {aiMethod === 'website' && (
            <div className="space-y-3">
              <label className="text-xs font-medium text-gray-600 block">Accelerator or program website URL</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  placeholder="https://ycombinator.com"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-sm"
                  disabled={aiProcessing}
                />
                <button
                  onClick={handleWebsiteScraping}
                  disabled={aiProcessing || !websiteUrl.trim()}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                >
                  {aiProcessing ? 'Processing…' : 'Extract'}
                </button>
              </div>
              <p className="text-xs text-gray-500">If URL fails, use “Paste bio text” and paste from the site instead.</p>
            </div>
          )}

          {/* Text Input - stack on mobile */}
          {aiMethod === 'text' && (
            <div className="space-y-3">
              <label className="text-xs font-medium text-gray-600 block">Paste accelerator or program bio text</label>
              <textarea
                placeholder="e.g. Y Combinator, seed accelerator. $500K for 7%. 3-month program, 200+ companies/batch. Portfolio: Stripe, Airbnb, DoorDash."
                value={acceleratorText}
                onChange={(e) => setAcceleratorText(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm min-h-[100px] resize-y"
                disabled={aiProcessing}
              />
              <div className="flex flex-col-reverse sm:flex-row gap-2">
                <button
                  onClick={() => {
                    setAiMethod('');
                    setWebsiteUrl('');
                    setAcceleratorText('');
                    setAiError('');
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTextExtraction}
                  disabled={aiProcessing || !acceleratorText.trim()}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                >
                  {aiProcessing ? 'Processing…' : 'Extract & fill form'}
                </button>
              </div>
            </div>
          )}

          {/* Error Display */}
          {aiError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{aiError}</p>
              {aiError.includes('Text Analysis') && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-orange-700 text-xs">Try “Paste bio text” above and paste from the website instead.</p>
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {!aiMethod && !aiError && (accelerator.name || accelerator.type) && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm">✅ Data loaded. Review and edit the fields below.</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Accelerator Name</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., Y Combinator" 
              value={accelerator.name} 
              onChange={e => setAccelerator({ ...accelerator, name: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Type</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., Seed Accelerator" 
              value={accelerator.type} 
              onChange={e => setAccelerator({ ...accelerator, type: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Location</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., San Francisco, CA" 
              value={accelerator.location} 
              onChange={e => setAccelerator({ ...accelerator, location: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Website</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="https://example.com" 
              value={accelerator.website} 
              onChange={e => setAccelerator({ ...accelerator, website: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Focus Areas</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., AI, SaaS, Consumer Tech" 
              value={accelerator.focus} 
              onChange={e => setAccelerator({ ...accelerator, focus: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Stage Focus</label>
            <select 
              className="border rounded-lg px-3 py-2" 
              value={accelerator.stage} 
              onChange={e => setAccelerator({ ...accelerator, stage: e.target.value })} 
              disabled={isDemo}
            >
              <option value="">Select Stage</option>
              <option value="Pre-seed">Pre-seed</option>
              <option value="Seed">Seed</option>
              <option value="Series A">Series A</option>
              <option value="Series B">Series B</option>
              <option value="Growth">Growth</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Investment Amount</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., $500K" 
              value={accelerator.investment} 
              onChange={e => setAccelerator({ ...accelerator, investment: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Equity Taken</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., 7%" 
              value={accelerator.equity} 
              onChange={e => setAccelerator({ ...accelerator, equity: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Program Duration</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., 3 months" 
              value={accelerator.duration} 
              onChange={e => setAccelerator({ ...accelerator, duration: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Batch Size</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., 200+ companies" 
              value={accelerator.batchSize} 
              onChange={e => setAccelerator({ ...accelerator, batchSize: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Success Rate</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., 85%" 
              value={accelerator.successRate} 
              onChange={e => setAccelerator({ ...accelerator, successRate: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Portfolio Companies</label>
            <input 
              className="border rounded-lg px-3 py-2" 
              placeholder="e.g., Stripe, Airbnb, Dropbox" 
              value={accelerator.portfolio} 
              onChange={e => setAccelerator({ ...accelerator, portfolio: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Application Deadline</label>
            <input 
              type="date"
              className="border rounded-lg px-3 py-2" 
              value={accelerator.applicationDeadline} 
              onChange={e => setAccelerator({ ...accelerator, applicationDeadline: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Program Start Date</label>
            <input 
              type="date"
              className="border rounded-lg px-3 py-2" 
              value={accelerator.programStart} 
              onChange={e => setAccelerator({ ...accelerator, programStart: e.target.value })} 
              disabled={isDemo}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Status</label>
            <select 
              className="border rounded-lg px-3 py-2" 
              value={accelerator.status} 
              onChange={e => setAccelerator({ ...accelerator, status: e.target.value })} 
              disabled={isDemo}
            >
              <option value="Researching">Researching</option>
              <option value="Applied">Applied</option>
              <option value="Interview">Interview</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">Tier</label>
            <select 
              className="border rounded-lg px-3 py-2" 
              value={accelerator.tier} 
              onChange={e => setAccelerator({ ...accelerator, tier: e.target.value })} 
              disabled={isDemo}
            >
              <option value="A">Tier A</option>
              <option value="B">Tier B</option>
              <option value="C">Tier C</option>
            </select>
          </div>
          <div className="flex flex-col gap-1 md:col-span-2">
            <label className="text-xs font-medium text-gray-700">Notes</label>
            <textarea 
              className="border rounded-lg px-3 py-2 h-24 resize-none" 
              placeholder="Additional notes about this accelerator..." 
              value={accelerator.notes} 
              onChange={e => setAccelerator({ ...accelerator, notes: e.target.value })} 
              disabled={isDemo}
            />
          </div>
        </div>

        <div className="pt-4 pb-2 sticky bottom-0 bg-white z-10">
          <div className="flex gap-2">
            <button 
              onClick={onSave} 
              disabled={creating || isDemo} 
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50"
            >
              {creating ? 'Saving...' : isDemo ? 'Demo - Cannot Save' : 'Save Accelerator'}
            </button>
            {accelerator.id && onDelete && !isDemo && (
              <button 
                onClick={onDelete}
                className="px-4 py-2 bg-red-500 text-white font-bold rounded-xl shadow-lg hover:bg-red-600 transition-all"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceleratorModal; 